import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Loader2, Printer, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ApiError } from "@/lib/api";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { resolveZplPrinterAtStation, useZplPrintLocation } from "@/lib/zplPrintLocation";
import { cn } from "@/lib/utils";
import { isReconSku } from "@integrator-src/manufacturing/configuration-sku-family";
import { useHubToast } from "@/components/ui/HubToast";
import {
  BOX_LABEL_GENERATE_BATCH_KEY,
  fetchBoxLabelGenerateBatch,
  fetchBoxLabelGenerateLine,
  printAllBoxLabelGenerateLine,
  testPrintBoxLabelGenerateLine,
  type BoxLabelGenerateLineRow,
} from "@/pages/manufacturing/boxLabelGenerateApi";
import {
  boxLabelLineScopeKey,
  boxLabelLineSerialsFingerprint,
  boxLabelLineSerialsFromQuery,
} from "@/pages/manufacturing/boxLabelGeneratePreviewState";
import { useBoxLabelGeneratePreviewLoader } from "@/pages/manufacturing/useBoxLabelGeneratePreviewLoader";

const BOX_LABEL_PAGE_HELP =
  "Print hub-owned FI-3C, FI-5A, and FI-5A-W box labels for a batch line without Pre-Pack pairing. Works for SGS and Suntronic batches. Every assembled unit on the line must already have a device label serial scanned before printing.";

function previewImageUrl(pngBase64: string): string {
  return `data:image/png;base64,${pngBase64}`;
}

function lineStatusBadge(line: BoxLabelGenerateLineRow): { tone: "success" | "warning" | "muted"; label: string } {
  if (!line.hub_box_supported) {
    return { tone: "muted", label: "No hub box label" };
  }
  if (line.ready_to_print) {
    return { tone: "success", label: "Ready" };
  }
  return { tone: "warning", label: "Not ready" };
}

export function BoxLabelGeneratePage() {
  const writesAllowed = useManufacturingScopeHubWrite();
  const { showToast } = useHubToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [batchIdInput, setBatchIdInput] = useState(searchParams.get("batch_id") ?? "");
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(() => {
    const parsed = Number.parseInt(searchParams.get("batch_id") ?? "", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [selectedBatchItemId, setSelectedBatchItemId] = useState<number | null>(() => {
    const parsed = Number.parseInt(searchParams.get("batch_item_id") ?? "", 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [userSelectedSerial, setUserSelectedSerial] = useState<string | null>(null);

  const lineScopeKey = boxLabelLineScopeKey(selectedBatchId, selectedBatchItemId);

  useEffect(() => {
    setUserSelectedSerial(null);
  }, [lineScopeKey]);

  const locationPrint = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.boxLabelGenerate,
    labelSize: "4x6",
    dpi: 300,
  });
  const { station, setStation } = locationPrint;

  const batchQuery = useQuery({
    queryKey: [...BOX_LABEL_GENERATE_BATCH_KEY, selectedBatchId],
    queryFn: () => fetchBoxLabelGenerateBatch(selectedBatchId!),
    enabled: selectedBatchId != null,
  });

  const selectedLine = useMemo(() => {
    const lines = batchQuery.data?.lines ?? [];
    if (selectedBatchItemId == null) return null;
    return lines.find((line) => line.batch_item_id === selectedBatchItemId) ?? null;
  }, [batchQuery.data?.lines, selectedBatchItemId]);

  const abbBox = (selectedLine?.sku ?? "").trim().toUpperCase().startsWith("ABB");
  const boxResolution = abbBox
    ? resolveZplPrinterAtStation(locationPrint.printersQuery.data?.printers ?? [], station, {
        labelSize: "4x6",
        dpi: 203,
      })
    : {
        printerName: locationPrint.selectedPrinter,
        unavailableMessage: locationPrint.unavailableMessage,
      };
  const selectedPrinter = boxResolution.printerName;
  const unavailableMessage = boxResolution.unavailableMessage;

  const hubBoxLines = useMemo(
    () => (batchQuery.data?.lines ?? []).filter((line) => line.hub_box_supported),
    [batchQuery.data?.lines],
  );

  const lineQuery = useQuery({
    queryKey: ["manufacturing", "box-label-generate", "line", selectedBatchId, selectedBatchItemId],
    queryFn: () => fetchBoxLabelGenerateLine(selectedBatchId!, selectedBatchItemId!),
    enabled: selectedBatchId != null && selectedBatchItemId != null,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const lineSerials = boxLabelLineSerialsFromQuery(lineQuery.data?.serials);

  const lineSerialsFingerprint = useMemo(
    () => boxLabelLineSerialsFingerprint(lineSerials),
    [lineSerials],
  );

  const lineReadyToPrint =
    lineQuery.data?.line.ready_to_print === true || selectedLine?.ready_to_print === true;

  const { effectiveSerial, preview, previewError, isPreviewLoading } = useBoxLabelGeneratePreviewLoader({
    batchId: selectedBatchId,
    batchItemId: selectedBatchItemId,
    lineReadyToPrint,
    lineSerialsFingerprint,
    lineSerials,
    printer: selectedPrinter,
    userSelectedSerial,
  });

  const testPrintMutation = useMutation({
    mutationFn: () =>
      testPrintBoxLabelGenerateLine({
        batchId: selectedBatchId!,
        batchItemId: selectedBatchItemId!,
        printer: selectedPrinter,
        deviceLabelSerial: effectiveSerial,
      }),
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message: `Sent test label for ${result.device_label_serial} to the printer.`,
      });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Test print failed.",
      });
    },
  });

  const printAllMutation = useMutation({
    mutationFn: () =>
      printAllBoxLabelGenerateLine({
        batchId: selectedBatchId!,
        batchItemId: selectedBatchItemId!,
        printer: selectedPrinter,
      }),
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message: `Printed ${String(result.printed_count)} of ${String(result.total_count)} box labels.`,
      });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Print all failed.",
      });
    },
  });

  function loadBatchFromInput(): void {
    const parsed = Number.parseInt(batchIdInput.trim(), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast({ tone: "error", message: "Enter a valid batch id." });
      return;
    }
    setSelectedBatchId(parsed);
    setSelectedBatchItemId(null);
    setUserSelectedSerial(null);
    const next = new URLSearchParams(searchParams);
    next.set("batch_id", String(parsed));
    next.delete("batch_item_id");
    setSearchParams(next, { replace: true });
  }

  function selectLine(line: BoxLabelGenerateLineRow): void {
    setSelectedBatchItemId(line.batch_item_id);
    setUserSelectedSerial(null);
    const next = new URLSearchParams(searchParams);
    if (selectedBatchId != null) {
      next.set("batch_id", String(selectedBatchId));
    }
    next.set("batch_item_id", String(line.batch_item_id));
    setSearchParams(next, { replace: true });
  }

  const printDisabled =
    !writesAllowed ||
    !lineReadyToPrint ||
    selectedPrinter.trim() === "" ||
    effectiveSerial === "" ||
    previewError != null ||
    testPrintMutation.isPending ||
    printAllMutation.isPending;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint={BOX_LABEL_PAGE_HELP}>Box label generate</PageTitleWithHelp>
        <ZplLocationSelect
          id="box-label-generate-location"
          size="compact"
          value={station}
          onChange={setStation}
          unavailableMessage={unavailableMessage}
        />
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Batch</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <Label htmlFor="box-label-batch-id">Batch id</Label>
            <Input
              id="box-label-batch-id"
              value={batchIdInput}
              onChange={(event) => setBatchIdInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadBatchFromInput();
              }}
              placeholder="e.g. 2146"
            />
          </div>
          <Button type="button" onClick={loadBatchFromInput}>
            Load batch
          </Button>
          {selectedBatchId != null ? (
            <Button
              type="button"
              variant="secondary"
              disabled={batchQuery.isFetching}
              onClick={() => void batchQuery.refetch()}
            >
              {batchQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden />
              )}
              Refresh
            </Button>
          ) : null}
        </CardBody>
      </Card>

      {batchQuery.isLoading && selectedBatchId != null ? (
        <p className="text-sm text-slate-500">Loading batch…</p>
      ) : null}

      {batchQuery.isError ? (
        <p className="text-sm text-rose-700">{(batchQuery.error as Error).message}</p>
      ) : null}

      {batchQuery.data != null ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(18rem,24rem)_1fr]">
          <Card className="self-start">
            <CardHeader>
              <CardTitle className="text-base">
                Batch {String(batchQuery.data.batch.batch_id)}
              </CardTitle>
              <p className="text-sm text-slate-600">
                {batchQuery.data.batch.ship_to_recipient ?? "No ship-to"}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <ManufacturingLocationChip locationId={batchQuery.data.batch.location_id} />
                {batchQuery.data.batch.mfr_ship_by_date != null ? (
                  <span>Mfr ship-by {batchQuery.data.batch.mfr_ship_by_date}</span>
                ) : null}
              </div>
            </CardHeader>
            <CardBody className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Batch lines</p>
              {hubBoxLines.length === 0 ? (
                <p className="text-sm text-slate-500">No hub box label SKUs on this batch.</p>
              ) : (
                <ul className="space-y-2">
                  {hubBoxLines.map((line) => {
                    const status = lineStatusBadge(line);
                    const selected = line.batch_item_id === selectedBatchItemId;
                    return (
                      <li key={line.batch_item_id}>
                        <button
                          type="button"
                          onClick={() => selectLine(line)}
                          className={cn(
                            "w-full rounded-md border px-3 py-2 text-left transition-colors",
                            selected
                              ? "border-brand-500 bg-brand-50"
                              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50",
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-slate-900">{line.sku}</p>
                              <p className="text-xs text-slate-500">
                                {line.build_order_number ?? "No build order"} · {String(line.device_label_scanned_count)}/
                                {String(line.quantity_assembled)} device labels
                              </p>
                            </div>
                            <Badge tone={status.tone}>{status.label}</Badge>
                          </div>
                          {line.block_reason != null && !line.ready_to_print ? (
                            <p className="mt-1 text-xs text-amber-800">{line.block_reason}</p>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Preview</CardTitle>
              {lineReadyToPrint ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={printDisabled}
                    onClick={() => testPrintMutation.mutate()}
                  >
                    {testPrintMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="h-4 w-4" aria-hidden />
                    )}
                    Test print
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={printDisabled}
                    onClick={() => printAllMutation.mutate()}
                  >
                    {printAllMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="h-4 w-4" aria-hidden />
                    )}
                    Print all ({String(selectedLine?.device_label_scanned_count ?? 0)})
                  </Button>
                </div>
              ) : null}
            </CardHeader>
            <CardBody className="space-y-4">
              {selectedLine == null ? (
                <p className="text-sm text-slate-500">Select a batch line to preview its box label.</p>
              ) : !lineReadyToPrint ? (
                <p className="text-sm text-amber-800">
                  {selectedLine.block_reason ?? "This line is not ready to print yet."}
                </p>
              ) : (
                <>
                  <div className="max-w-md">
                    <Label htmlFor="box-label-preview-serial">Preview device label serial</Label>
                    <select
                      id="box-label-preview-serial"
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                      value={effectiveSerial}
                      disabled={lineSerials.length === 0}
                      onChange={(event) => setUserSelectedSerial(event.target.value)}
                    >
                      {lineSerials.map((row) => (
                        <option key={row.serial_fci_id} value={row.device_label_serial}>
                          {row.device_label_serial}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      Choose a serial for preview and test print. Print all sends one label per scanned device on this
                      line.
                    </p>
                  </div>

                  {isPreviewLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Rendering preview…
                    </div>
                  ) : null}

                  {previewError != null ? (
                    <div className="space-y-2">
                      <p className="text-sm text-rose-700">{previewError.message}</p>
                      {isReconSku(selectedLine.sku) ? (
                        <p className="text-sm text-amber-800">
                          FI-3C box labels show an accessory table only when
                          generate_description_parts returns label_table rows for this SKU.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  {lineSerials.length === 0 && lineQuery.isFetching ? (
                    <p className="text-sm text-slate-500">Loading device serials for this line…</p>
                  ) : null}

                  {preview?.png_base64 != null ? (
                    <div className="overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3">
                      <img
                        src={previewImageUrl(preview.png_base64)}
                        alt={`Box label preview for ${preview.device_label_serial}`}
                        className="mx-auto max-h-[32rem] w-auto shadow-sm"
                      />
                      <p className="mt-2 text-center text-xs text-slate-500">
                        {preview.finished_sku} · {preview.device_label_serial} ·{" "}
                        {String(preview.preview_dpi)} dpi preview
                      </p>
                    </div>
                  ) : null}

                  {!writesAllowed ? (
                    <p className="text-sm text-slate-500">You have read-only access; printing is disabled.</p>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
